import React, { useMemo } from 'react';
import { Trophy, TrendingUp } from 'lucide-react';

const TopProjectsTable = ({ data }) => {
  const topProjects = useMemo(() => {
    if (!data || data?.length === 0) return [];

    return [...data]?.sort((a, b) => (b?.lucroPercent || 0) - (a?.lucroPercent || 0))?.slice(0, 10)?.map((project, index) => ({
        ...project,
        rank: index + 1
      }));
  }, [data]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-yellow-500';
    if (rank === 2) return 'text-gray-400';
    if (rank === 3) return 'text-amber-600';
    return 'text-muted-foreground';
  };

  const getMarginColor = (margin) => {
    if (margin >= 20) return 'text-success';
    if (margin >= 10) return 'text-primary';
    if (margin >= 0) return 'text-warning';
    return 'text-destructive';
  };

  if (topProjects?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <Trophy className="h-5 w-5 text-primary" />
          <h3 className="text-lg sm:text-xl font-semibold text-foreground">Projetos Mais Rentáveis</h3>
        </div>
        <div className="text-center py-12 text-muted-foreground">
          <p>Nenhum projeto disponível</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className="h-5 w-5 text-primary" />
        <h3 className="text-lg sm:text-xl font-semibold text-foreground">Projetos Mais Rentáveis</h3>
      </div>

      {/* Mobile View */}
      <div className="block sm:hidden space-y-3">
        {topProjects?.map((project) => (
          <div key={project?.id} className="border border-border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className={`text-2xl font-bold ${getRankColor(project?.rank)}`}>
                  #{project?.rank}
                </span>
                <div>
                  <p className="font-semibold text-foreground">{project?.projectName}</p>
                  <p className="text-xs text-muted-foreground">{project?.userName}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <TrendingUp className={`h-4 w-4 ${getMarginColor(project?.lucroPercent)}`} />
                <span className={`text-sm font-bold ${getMarginColor(project?.lucroPercent)}`}>
                  {project?.lucroPercent?.toFixed(1)}%
                </span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <p className="text-muted-foreground">Receita</p>
                <p className="font-semibold text-foreground">{formatCurrency(project?.totalObra)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Lucro</p>
                <p className="font-semibold text-success">{formatCurrency(project?.lucroLiquido)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop View */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Rank</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Projeto</th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">Cliente</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Receita</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Lucro</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">Margem</th>
            </tr>
          </thead>
          <tbody>
            {topProjects?.map((project) => (
              <tr key={project?.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    {project?.rank <= 3 && <Trophy className={`h-4 w-4 ${getRankColor(project?.rank)}`} />}
                    <span className={`font-bold ${getRankColor(project?.rank)}`}>#{project?.rank}</span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <p className="font-medium text-foreground">{project?.projectName}</p>
                </td>
                <td className="py-3 px-4">
                  <p className="text-sm text-muted-foreground">{project?.userName}</p>
                </td>
                <td className="py-3 px-4 text-right">
                  <p className="font-medium text-foreground">{formatCurrency(project?.totalObra)}</p>
                </td>
                <td className="py-3 px-4 text-right">
                  <p className="font-medium text-success">{formatCurrency(project?.lucroLiquido)}</p>
                </td>
                <td className="py-3 px-4 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <TrendingUp className={`h-4 w-4 ${getMarginColor(project?.lucroPercent)}`} />
                    <span className={`font-bold ${getMarginColor(project?.lucroPercent)}`}>
                      {project?.lucroPercent?.toFixed(1)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopProjectsTable;